import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";

const REGIONS = ["north", "west", "south", "east"];
const REGION_LABELS = { north: "North", west: "West", south: "South", east: "East" };
const FIELDS = [
  { key: "aopTarget", label: "AOP Target" },
  { key: "target", label: "Target" },
  { key: "aopCatchment", label: "AOP Catchment" },
];

const emptyRegion = () => ({ aopTarget: 0, target: 0, aopCatchment: 0 });

const emptyForm = () => ({
  fiscalYear: "",
  dateFrom: "",
  dateTo: "",
  north: emptyRegion(),
  west: emptyRegion(),
  south: emptyRegion(),
  east: emptyRegion(),
});

const ReportConfigSetup = () => {
  const [configs, setConfigs] = useState([]);
  const [selectedFY, setSelectedFY] = useState("__new__");
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_REPORT_CONFIG}`)
      .then((res) => {
        const list = Array.isArray(res) ? res : res ? [res] : [];
        setConfigs(list);
      })
      .catch(() => toast.error("Failed to load configs"))
      .finally(() => setLoading(false));
  }, []);

  const handleFYSelect = (fy) => {
    setSelectedFY(fy);
    if (fy === "__new__") {
      setForm(emptyForm());
      return;
    }
    const config = configs.find((c) => c.fiscalYear === fy);
    if (config) {
      setForm({
        fiscalYear: config.fiscalYear,
        dateFrom: config.dateFrom ? config.dateFrom.slice(0, 10) : "",
        dateTo: config.dateTo ? config.dateTo.slice(0, 10) : "",
        north: { ...emptyRegion(), ...config.north },
        west: { ...emptyRegion(), ...config.west },
        south: { ...emptyRegion(), ...config.south },
        east: { ...emptyRegion(), ...config.east },
      });
    }
  };

  const handleRegionField = (region, field, value) => {
    setForm((prev) => ({
      ...prev,
      [region]: { ...prev[region], [field]: Number(value) || 0 },
    }));
  };

  const handleSave = () => {
    if (!form.fiscalYear || !form.dateFrom || !form.dateTo) {
      toast.error("Fiscal Year, Date From, and Date To are required");
      return;
    }
    setSaving(true);
    invokeApi(HTTP_METHODS.POST, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_REPORT_CONFIG}`, form)
      .then((res) => {
        toast.success("Config saved successfully");
        setConfigs((prev) => {
          const idx = prev.findIndex((c) => c.fiscalYear === res.fiscalYear);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = res;
            return updated;
          }
          return [...prev, res];
        });
        setSelectedFY(res.fiscalYear);
      })
      .catch(() => toast.error("Failed to save config"))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Pipeline Report Config
        </Typography>
        <Select
          size="small"
          value={selectedFY}
          onChange={(e) => handleFYSelect(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="__new__">+ New Fiscal Year</MenuItem>
          {configs.map((c) => (
            <MenuItem key={c.fiscalYear} value={c.fiscalYear}>
              {c.fiscalYear}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Fiscal Year"
            size="small"
            fullWidth
            placeholder="e.g. FY27"
            value={form.fiscalYear}
            onChange={(e) => setForm((f) => ({ ...f, fiscalYear: e.target.value }))}
            disabled={selectedFY !== "__new__"}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Date From"
            size="small"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.dateFrom}
            onChange={(e) => setForm((f) => ({ ...f, dateFrom: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Date To"
            size="small"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.dateTo}
            onChange={(e) => setForm((f) => ({ ...f, dateTo: e.target.value }))}
          />
        </Grid>
      </Grid>

      <Box sx={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", minWidth: 500 }}>
          <thead>
            <tr style={{ background: "#1a3652" }}>
              <th style={thStyle}>Region</th>
              {FIELDS.map((f) => (
                <th key={f.key} style={thStyle}>{f.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REGIONS.map((region) => (
              <tr key={region}>
                <td style={tdLabel}>{REGION_LABELS[region]}</td>
                {FIELDS.map((f) => (
                  <td key={f.key} style={tdCell}>
                    <TextField
                      size="small"
                      type="number"
                      value={form[region][f.key]}
                      onChange={(e) => handleRegionField(region, f.key, e.target.value)}
                      inputProps={{ min: 0, style: { textAlign: "center" } }}
                      sx={{ width: 90 }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Config"}
        </Button>
      </Box>
    </Box>
  );
};

const thStyle = {
  color: "#fff",
  padding: "8px 16px",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "0.82rem",
  border: "1px solid #2a4d6e",
};

const tdLabel = {
  padding: "8px 16px",
  fontWeight: "bold",
  fontSize: "0.82rem",
  border: "1px solid #ddd",
  background: "#f5f5f5",
};

const tdCell = {
  padding: "6px 10px",
  textAlign: "center",
  border: "1px solid #ddd",
};

export default ReportConfigSetup;
